import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { useMutation } from "@tanstack/react-query";
import type { PresignedClient } from "@/lib/file-storage/types";
import type { AttachmentItem, PendingDelete } from "../../types/attachments";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";

interface QuestionnaireAttachmentDeletionProps {
  presignedClient: PresignedClient;
  name: string;
  onFileDeleted: (s3Key: string, index: number) => void;
}

export const useQuestionnaireAttachmentDeletion = ({
  presignedClient,
  name,
  onFileDeleted,
}: QuestionnaireAttachmentDeletionProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );
  const operationProgress = useAsyncOperationProgress();

  const deleteMutation = useMutation({
    mutationFn: async (deleteData: PendingDelete) => {
      operationProgress.initialize([
        {
          id: deleteData.s3Key,
          label: deleteData.filename,
        },
      ]);
      operationProgress.startProcessing();
      operationProgress.setCurrentItem(deleteData.filename);

      try {
        await presignedClient.remove(name, {
          filename: deleteData.filename,
          s3Key: deleteData.s3Key,
        });
        operationProgress.markItemProcessed(deleteData.s3Key, true);
        return deleteData;
      } catch (error) {
        operationProgress.markItemProcessed(deleteData.s3Key, false);
        throw error;
      } finally {
        operationProgress.complete();
      }
    },
    onSuccess: (deleteData) => {
      onFileDeleted(deleteData.s3Key, deleteData.index);
      setPendingDelete(null);
    },
    onError: (_, deleteData) => {
      toast({
        title: t("fileAttachments.deleteErrorTitle"),
        description: t("fileAttachments.deleteError", {
          filename: deleteData.filename,
        }),
        variant: "destructive",
      });
      setPendingDelete(null);
    },
  });

  const handleRemove = useCallback((item: AttachmentItem) => {
    setPendingDelete({
      index: item.index,
      filename: item.field.filename,
      s3Key: item.field.s3_key,
    });
  }, []);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete);
  }, [pendingDelete, deleteMutation]);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  return {
    pendingDelete,
    confirmDelete,
    cancelDelete,
    handleRemove,
    isDeleting: deleteMutation.isPending,
    operationProgress,
  };
};
